import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Camera, Check, Fuel, X } from 'lucide-react'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/Card'
import Input from '../../components/ui/Input'
import Button from '../../components/ui/Button'
import Alert from '../../components/ui/Alert'
import CalendarPicker from '../../components/ui/CalendarPicker'
import Loading from '../../components/ui/Loading'
import { discountService, hourmeterService } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

interface FuelFormData {
  date: string
  gallons: string
  observations: string
  photo: File | null
}

const parseNumber = (value: string) => Number(value.replace(',', '.'))

const getNormalizedDateString = (date: Date) => {
  const offset = date.getTimezoneOffset() * 60 * 1000
  return new Date(date.getTime() - offset).toISOString().split('T')[0]
}

const initialForm = (): FuelFormData => ({
  date: getNormalizedDateString(new Date()),
  gallons: '',
  observations: '',
  photo: null,
})

const CreateFuelLoad: React.FC = () => {
  const navigate = useNavigate()
  const { operator } = useAuth()
  const [formData, setFormData] = useState<FuelFormData>(initialForm)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [dealId, setDealId] = useState<number | null>(null)
  const [dealStatus, setDealStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [dealMessage, setDealMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [submitMessage, setSubmitMessage] = useState('')
  const [errors, setErrors] = useState<Partial<Record<keyof FuelFormData, string>>>({})

  const isDisabled = dealStatus !== 'ready' || isSubmitting

  useEffect(() => {
    if (!formData.photo) {
      setPhotoPreview(null)
      return
    }
    const url = URL.createObjectURL(formData.photo)
    setPhotoPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [formData.photo])

  useEffect(() => {
    let alive = true
    if (!operator?.id) return
    setDealStatus('loading')
    hourmeterService
      .getActiveDealForOperator(operator.id)
      .then((res) => {
        if (!alive) return
        const id = res?.dealId ?? null
        if (!id) {
          setDealStatus('error')
          setDealMessage('No se encontró una oferta activa para cargar combustible.')
          return
        }
        setDealId(id)
        setDealStatus('ready')
      })
      .catch(() => {
        if (!alive) return
        setDealStatus('error')
        setDealMessage('No se pudo consultar la oferta activa. Intenta de nuevo.')
      })
    return () => {
      alive = false
    }
  }, [operator?.id])

  const handleField = <K extends keyof FuelFormData>(key: K, value: FuelFormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: undefined }))
  }

  const handlePhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    handleField('photo', file)
  }

  const validate = (): boolean => {
    const next: Partial<Record<keyof FuelFormData, string>> = {}
    if (!formData.photo) next.photo = 'Adjunta la foto del comprobante de combustible.'
    const gallons = parseNumber(formData.gallons)
    if (!formData.gallons || Number.isNaN(gallons) || gallons <= 0) {
      next.gallons = 'Ingresa la cantidad de galones recibidos.'
    } else if (gallons > 10000) {
      next.gallons = 'Cantidad fuera de rango.'
    }
    if (!formData.date) next.date = 'Selecciona la fecha del cargue.'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const resetForm = () => {
    setFormData(initialForm())
    setErrors({})
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!validate() || !operator || !dealId || !formData.photo) return
    setIsSubmitting(true)
    setSubmitStatus('idle')
    setSubmitMessage('')
    try {
      const res = await discountService.createFuelDiscount(
        {
          operatorId: operator.id,
          dealId,
          gallons: parseNumber(formData.gallons),
          observations: formData.observations || undefined,
          date: formData.date,
        },
        formData.photo,
      )
      if (res.status === 'success') {
        setSubmitStatus('success')
        setSubmitMessage(res.message || 'Cargue de combustible registrado correctamente.')
        resetForm()
      } else {
        setSubmitStatus('error')
        setSubmitMessage(res.message || 'No se pudo registrar el cargue.')
      }
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message ??
        (err as { message?: string }).message ??
        'Error al registrar el cargue.'
      setSubmitStatus('error')
      setSubmitMessage(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (dealStatus === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loading size="lg" text="Cargando oferta activa..." />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-retri-blue text-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-2">
          <Fuel className="w-5 h-5" />
          <h1 className="text-lg font-semibold">Cargue de Combustible</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {dealStatus === 'error' ? (
          <Alert variant="error" className="mb-4">
            {dealMessage}
          </Alert>
        ) : null}

        {submitStatus !== 'idle' && submitMessage ? (
          <Alert variant={submitStatus === 'success' ? 'success' : 'error'} className="mb-4">
            {submitMessage}
          </Alert>
        ) : null}

        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Registro del cargue</CardTitle>
              <CardDescription>
                Solo necesitas la cantidad de galones y una foto del comprobante. El costo se calcula
                automáticamente según el pricing del negocio.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <CalendarPicker
                  selectedDate={formData.date}
                  onChange={(v: string) => handleField('date', v)}
                  disabled={isDisabled}
                />
                {errors.date ? <p className="mt-2 text-sm text-red-600">{errors.date}</p> : null}
              </div>

              <Input
                label="Galones recibidos"
                type="text"
                inputMode="decimal"
                placeholder="Ej. 12.5"
                value={formData.gallons}
                onChange={(e) => handleField('gallons', e.target.value)}
                disabled={isDisabled}
                error={errors.gallons}
              />

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Observaciones (opcional)
                </label>
                <textarea
                  className="flex min-h-[96px] w-full rounded-md border-2 border-gray-400 bg-white px-3 py-2 text-base shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-retri-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={formData.observations}
                  onChange={(e) => handleField('observations', e.target.value)}
                  disabled={isDisabled}
                  maxLength={500}
                  placeholder="Detalles adicionales del cargue..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Foto del comprobante
                </label>
                {photoPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={photoPreview}
                      alt="Comprobante"
                      className="max-h-64 rounded-md border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => handleField('photo', null)}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                      aria-label="Quitar foto"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center gap-2 h-40 border-2 border-dashed border-gray-400 rounded-md cursor-pointer hover:bg-gray-100 transition-colors">
                    <Camera className="w-8 h-8 text-gray-500" />
                    <span className="text-sm text-gray-600">Tomar o subir foto</span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={handlePhoto}
                      disabled={isDisabled}
                    />
                  </label>
                )}
                {errors.photo ? <p className="mt-2 text-sm text-red-600">{errors.photo}</p> : null}
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                disabled={isSubmitting}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isDisabled}
                className="w-full sm:w-auto"
              >
                {isSubmitting ? (
                  <Loading size="sm" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Registrar cargue
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </main>
    </div>
  )
}

export default CreateFuelLoad
